'use es6';

import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useTracker } from '../../utils/hooks/useTracker';
import { isEditableReport } from '../../lib/report-logic';
import UpgradePointButton from '../shared/upgradePoints/AsyncUpgradePointButton';
import { ATTRIBUTION_BUILDER_ENTERPRISE_UPGRADE_KEYS, ATTRIBUTION_BUILDER_PRO_UPGRADE_KEYS, REPORT_BUILDER_UPGRADE_KEYS, UPGRADE_POINT_LOCATION_VALUES } from '../shared/upgradePoints/upgradePointKeys';
import useEditReportProblem from '../shared/problemHooks/useEditReportProblem';
import useCustomizeReportProblem from '../shared/problemHooks/useCustomizeReportProblem';
import ActionButton from '../ActionButton';
import { LACKS_ATTRIBUTION_REPORTING, LACKS_CUSTOM_REPORTING } from '../problems';
import { ATTRIBUTION_UPGRADE_PRODUCT_TYPES, getAttributionReportUpgradeProduct } from '../shared/upgradePoints/upgradeProducts';
import { EVENTS } from '../../utils/usageTracker';
import { getDefaultSavedReportActionEventProperties } from '../report-action-wrapper';
import openReportInBuilder from './openReportInBuilder';
import { useIsJitaUser } from '../../access/scopes/hooks/useHasScope';
import { useUserRoleForReport } from '../../utils/trackingUtils';
import { noop } from '../shared/noop';
import { getProblemTooltipUse } from '../problemHelpers';
import { jsx as _jsx } from "react/jsx-runtime";
const labelPropDefault = /*#__PURE__*/_jsx(FormattedMessage, {
  message: "reporting-action-components.actions.editReport.button.text"
});
const EditReportButton = ({
  appUsageTracker,
  dashboardId,
  disabled: isDisabledViaParentApp = false,
  label = labelPropDefault,
  onButtonClick = noop,
  report,
  showOnDisable = true,
  tooltipMessage: appDefinedTooltip,
  tooltipPlacement,
  upgradeLocation,
  trackingProperties = {},
  size,
  use
}) => {
  const usageTracker = useTracker(appUsageTracker);
  const {
    problem: editProblem,
    disabledMessage: editDisabledMessage
  } = useEditReportProblem({
    report,
    shouldBecomeCustom: true
  });
  const {
    problem: customizeProblem,
    disabledMessage: customizeDisabledMessage
  } = useCustomizeReportProblem({
    report
  });
  const problem = editProblem || customizeProblem;
  const disabledMessage = editDisabledMessage || customizeDisabledMessage;
  const isJitaUser = useIsJitaUser();
  const isDisabled = !isJitaUser && (isDisabledViaParentApp || !!problem);
  const userRole = useUserRoleForReport(report);
  const handleEditReport = () => {
    if (usageTracker) {
      usageTracker.track(EVENTS.clickEditInBuilder, Object.assign({
        actionType: 'edit',
        action: 'edit-in-builder',
        role: userRole
      }, getDefaultSavedReportActionEventProperties(report), trackingProperties));
    }
    onButtonClick();
    openReportInBuilder({
      dashboardId,
      report
    });
  };
  if (!isEditableReport(report) || isDisabled && !showOnDisable) {
    return null;
  }
  if (upgradeLocation) {
    if (problem === LACKS_CUSTOM_REPORTING) {
      return /*#__PURE__*/_jsx(UpgradePointButton, {
        label: label,
        upgradeKey: REPORT_BUILDER_UPGRADE_KEYS[upgradeLocation],
        use: use,
        size: size
      });
    }
    if (problem === LACKS_ATTRIBUTION_REPORTING) {
      const requiresEnterprise = getAttributionReportUpgradeProduct(report) === ATTRIBUTION_UPGRADE_PRODUCT_TYPES.ENTERPRISE;
      return /*#__PURE__*/_jsx(UpgradePointButton, {
        label: label,
        upgradeKey: requiresEnterprise ? ATTRIBUTION_BUILDER_ENTERPRISE_UPGRADE_KEYS[upgradeLocation] : ATTRIBUTION_BUILDER_PRO_UPGRADE_KEYS[upgradeLocation],
        use: use,
        size: size
      });
    }
  }
  return /*#__PURE__*/_jsx(ActionButton, {
    dataTestId: "report-action-edit",
    label: label,
    disabled: isDisabled,
    tooltip: appDefinedTooltip || disabledMessage,
    tooltipPlacement: tooltipPlacement,
    tooltipUse: getProblemTooltipUse(problem),
    onClick: handleEditReport,
    size: size,
    use: use
  });
};
EditReportButton.propTypes = {
  appUsageTracker: PropTypes.object.isRequired,
  dashboardId: PropTypes.number,
  disabled: PropTypes.bool.isRequired,
  label: PropTypes.element,
  onButtonClick: PropTypes.func,
  report: PropTypes.object.isRequired,
  showOnDisable: PropTypes.bool,
  tooltipMessage: PropTypes.node,
  tooltipPlacement: PropTypes.string,
  upgradeLocation: PropTypes.oneOf(UPGRADE_POINT_LOCATION_VALUES),
  trackingProperties: PropTypes.object
};
export default EditReportButton;